import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Search, Package, Settings, RefreshCw, MapPin, Zap, Plus, Layers, AlertTriangle, Filter, X, Trash2, PackageX, PackageCheck, Loader2, Tags, TrendingUp, Building2, Calculator, Percent } from "lucide-react";
import { toast } from "sonner";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import ShelfTalkerModal from "@/components/ShelfTalkerModal";
import StockAdjustmentModal from "@/components/StockAdjustmentModal";
import { BulkProductGroupModal } from "@/components/products/BulkProductGroupModal";
import { UnusedProductsCleanup } from "@/components/products/UnusedProductsCleanup";
import { TopMoversModal } from "@/components/products/TopMoversModal";
import BulkAssignSupplierModal from "@/components/products/BulkAssignSupplierModal";
import BulkStaffDiscountModal from "@/components/products/BulkStaffDiscountModal";
import ArchivedProductsModal from "@/components/products/ArchivedProductsModal";
import DuplicateCategoriesDialog from "@/components/products/DuplicateCategoriesDialog";
import DuplicateDepartmentsDialog from "@/components/products/DuplicateDepartmentsDialog";
import { Archive, Wand2, CaseSensitive, Gauge } from "lucide-react";
import { DepartmentFixerModal } from "@/components/products/DepartmentFixerModal";
import { TidyDepartmentNamesModal } from "@/components/products/TidyDepartmentNamesModal";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Re-export LocationInput utilities for backward compatibility
export { LocationInput, SplitLocationInput, formatLocation, parseLocation, validateLocation } from "@/components/LocationInput";

// Import refactored components
import {
  ProductList,
  ProductDetailModal,
  QuickLocationEditor,
  QuickCategoryEditor,
  BulkCategoryDepartmentModal,
  DeleteProductDialog,
  FormulaSettingsDialog,
  API,
  PRODUCTS_PER_PAGE,
  defaultProduct,
} from "@/components/products";

export const ProductMaster = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [shelfBins, setShelfBins] = useState([]);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [quickLocationOpen, setQuickLocationOpen] = useState(false);
  const [quickCategoryOpen, setQuickCategoryOpen] = useState(false);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [deptFixerOpen, setDeptFixerOpen] = useState(false);
  const [tidyNamesOpen, setTidyNamesOpen] = useState(false);
  const { isAdmin } = useAuth();
  const [stockAdjustmentOpen, setStockAdjustmentOpen] = useState(false);
  const [topMoversOpen, setTopMoversOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  
  // Filter state
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterShelfBin, setFilterShelfBin] = useState("");
  const [filterImportance, setFilterImportance] = useState("");
  const [autoImportanceBusy, setAutoImportanceBusy] = useState(false);
  const [filterNoStock, setFilterNoStock] = useState(false);
  const [filterHasStock, setFilterHasStock] = useState(false);
  const [filterMarkupOperator, setFilterMarkupOperator] = useState("<=");
  const [filterMarkupValue, setFilterMarkupValue] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  // "exclude" hides direct-order items (default Stock Master view);
  // "only" shows just direct-order items (Direct Items view).
  const [directOrderMode, setDirectOrderMode] = useState("exclude");
  // When true, list is filtered to products awaiting stock-level review
  // (first sold → auto-seeded 1/1/1 → user hasn't confirmed/edited yet).
  const [needsReviewMode, setNeedsReviewMode] = useState(false);
  const [showInternalConsumables, setShowInternalConsumables] = useState(false);
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Bulk VAT category
  const [bulkVatOpen, setBulkVatOpen] = useState(false);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [bulkVatTarget, setBulkVatTarget] = useState("Zero-rated 0%");
  const [bulkVatBusy, setBulkVatBusy] = useState(false);
  
  // Quick Category Editor state - lifted up to preserve between modal open/close
  const [keepCategoryLocked, setKeepCategoryLocked] = useState(false);
  const [lockedCategoryValue, setLockedCategoryValue] = useState("");
  const [lockedDepartmentValue, setLockedDepartmentValue] = useState("");
  
  const [formulaSettings, setFormulaSettings] = useState({
    minimum_level_multiplier: 2.0,
    reorder_level_multiplier: 4.0,
    maximum_level_multiplier: 6.0
  });
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // Search debounce
  const searchTimeoutRef = useRef(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Print Stickers
  const [printStickersOpen, setPrintStickersOpen] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState([]);
  
  // Bulk Product Group Assignment
  const [bulkGroupModalOpen, setBulkGroupModalOpen] = useState(false);
  const [bulkSupplierModalOpen, setBulkSupplierModalOpen] = useState(false);
  const [bulkStaffDiscountModalOpen, setBulkStaffDiscountModalOpen] = useState(false);
  const [duplicateCategoriesOpen, setDuplicateCategoriesOpen] = useState(false);
  const [duplicateDepartmentsOpen, setDuplicateDepartmentsOpen] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategoriesDepartments();
    fetchFormulaSettings();
    fetchSuppliers();
    fetchShelfBins();
  }, []);

  const fetchProducts = async (search = "", pageNum = 1, append = false, filters = {}) => {
    setLoading(true);
    try {
      // Use the paginated endpoint which properly handles limit and returns count
      let url = `${API}/products/paginated?skip=${(pageNum - 1) * PRODUCTS_PER_PAGE}&limit=${PRODUCTS_PER_PAGE}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      // Add filter parameters
      if (filters.supplier || filterSupplier) {
        url += `&supplier=${encodeURIComponent(filters.supplier || filterSupplier)}`;
      }
      if (filters.category || filterCategory) {
        url += `&category=${encodeURIComponent(filters.category || filterCategory)}`;
      }
      if (filters.department || filterDepartment) {
        url += `&department=${encodeURIComponent(filters.department || filterDepartment)}`;
      }
      if (filters.shelf_bin || filterShelfBin) {
        url += `&shelf_bin=${encodeURIComponent(filters.shelf_bin || filterShelfBin)}`;
      }
      const importanceFilter = filters.order_importance !== undefined ? filters.order_importance : filterImportance;
      if (importanceFilter) {
        url += `&order_importance=${encodeURIComponent(importanceFilter)}`;
      }
      // No stock filter
      const noStockFilter = filters.no_stock !== undefined ? filters.no_stock : filterNoStock;
      if (noStockFilter) {
        url += `&no_stock=true`;
      }
      // Has stock filter (mirror of no_stock)
      const hasStockFilter = filters.has_stock !== undefined ? filters.has_stock : filterHasStock;
      if (hasStockFilter) {
        url += `&has_stock=true`;
      }

      const markupOperatorFilter = filters.markup_operator !== undefined ? filters.markup_operator : filterMarkupOperator;
      const markupValueFilterRaw = filters.markup_value !== undefined ? filters.markup_value : filterMarkupValue;
      const hasMarkupValue = markupValueFilterRaw !== "" && markupValueFilterRaw !== null && markupValueFilterRaw !== undefined;
      if (markupOperatorFilter && hasMarkupValue && !Number.isNaN(Number(markupValueFilterRaw))) {
        url += `&markup_operator=${encodeURIComponent(markupOperatorFilter)}`;
        url += `&markup_value=${encodeURIComponent(markupValueFilterRaw)}`;
      }

      // Direct-order (special order) split: send the active mode (defaults to
      // "exclude" so the standard Stock Master never mixes one-off items with
      // stocked products).
      const directMode = filters.direct_order !== undefined ? filters.direct_order : directOrderMode;
      if (directMode) {
        url += `&direct_order=${encodeURIComponent(directMode)}`;
      }

      // Needs-review filter — products auto-seeded 1/1/1 on first sale.
      const needsReview = filters.needs_stock_review !== undefined ? filters.needs_stock_review : needsReviewMode;
      if (needsReview) {
        url += `&needs_stock_review=true`;
      }

      // The "Internal Items" toolbar chip now shows ONLY internal-use
      // consumables when active (till rolls / paper / toner etc.) — matches
      // the user expectation of "click Internal Items → see just those".
      // Off ⇒ hide internals from the normal listing (default).
      // Callers can still explicitly pass ``include_internal_consumables`` in
      // ``filters`` to force the OLD "mix internals in with everything else"
      // behaviour (nothing in the UI does that any more, but leave the escape
      // hatch for scripts / potential future views).
      const forceInclude =
        filters.include_internal_consumables !== undefined
          ? filters.include_internal_consumables
          : false;
      if (showInternalConsumables) {
        url += `&internal_only=true`;
      } else if (forceInclude) {
        url += `&include_internal_consumables=true`;
      }
      
      const response = await axios.get(url);
      const { products: newProducts, total, has_more } = response.data;
      
      if (append) {
        setProducts(prev => [...prev, ...newProducts]);
        setFilteredProducts(prev => [...prev, ...newProducts]);
      } else {
        setProducts(newProducts);
        setFilteredProducts(newProducts);
        // Clear selection when filter changes
        setSelectedProducts(new Set());
      }
      
      setTotalProducts(total);
      setHasMore(has_more);
      setPage(pageNum);

      // Fire a batch sparkline fetch (non-blocking) — adds a 6-month sales
      // velocity series to each product so the inline mini-chart renders.
      const plus = newProducts.map(p => p.plu_code).filter(Boolean);
      if (plus.length > 0) {
        axios.post(`${API}/products/sales-sparkline`, { plu_codes: plus })
          .then(({ data }) => {
            const apply = (list) => list.map(p =>
              data[p.plu_code] ? { ...p, sales_sparkline: data[p.plu_code] } : p
            );
            setProducts(prev => apply(prev));
            setFilteredProducts(prev => apply(prev));
          })
          .catch(() => { /* silent — sparkline is decorative */ });
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoriesDepartments = async () => {
    try {
      const response = await axios.get(`${API}/categories-departments`);
      setCategories(response.data.categories || []);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data || []);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  };

  const fetchShelfBins = async () => {
    try {
      const response = await axios.get(`${API}/products/shelf-bins`);
      setShelfBins(response.data || []);
    } catch (error) {
      console.error("Error fetching shelf/bins:", error);
    }
  };

  const fetchFormulaSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/stock-formula`);
      if (response.data) {
        setFormulaSettings(response.data);
      }
    } catch (error) {
      console.error("Error fetching formula settings:", error);
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchProducts(value, 1, false);
    }, 300);
  };

  const handleFilterChange = (filterType, value) => {
    // The two stock filters are mutually exclusive — enabling one clears the other.
    let nextNoStock = filterNoStock;
    let nextHasStock = filterHasStock;
    if (filterType === 'no_stock') {
      nextNoStock = value;
      if (value) nextHasStock = false;
    }
    if (filterType === 'has_stock') {
      nextHasStock = value;
      if (value) nextNoStock = false;
    }

    const newFilters = {
      supplier: filterType === 'supplier' ? value : filterSupplier,
      category: filterType === 'category' ? value : filterCategory,
      department: filterType === 'department' ? value : filterDepartment,
      shelf_bin: filterType === 'shelf_bin' ? value : filterShelfBin,
      order_importance: filterType === 'order_importance' ? value : filterImportance,
      no_stock: nextNoStock,
      has_stock: nextHasStock,
      markup_operator: filterType === 'markup_operator' ? value : filterMarkupOperator,
      markup_value: filterType === 'markup_value' ? value : filterMarkupValue,
    };

    if (filterType === 'supplier') setFilterSupplier(value);
    if (filterType === 'category') setFilterCategory(value);
    if (filterType === 'department') setFilterDepartment(value);
    if (filterType === 'shelf_bin') setFilterShelfBin(value);
    if (filterType === 'order_importance') setFilterImportance(value);
    if (filterType === 'no_stock' || filterType === 'has_stock') {
      setFilterNoStock(nextNoStock);
      setFilterHasStock(nextHasStock);
    }
    if (filterType === 'markup_operator') setFilterMarkupOperator(value);
    if (filterType === 'markup_value') setFilterMarkupValue(value);
    
    fetchProducts(searchQuery, 1, false, newFilters);
  };

  const clearAllFilters = () => {
    setFilterSupplier("");
    setFilterCategory("");
    setFilterDepartment("");
    setFilterShelfBin("");
    setFilterImportance("");
    setFilterNoStock(false);
    setFilterHasStock(false);
    setFilterMarkupOperator("<=");
    setFilterMarkupValue("");
    fetchProducts(searchQuery, 1, false, {
      supplier: "",
      category: "",
      department: "",
      shelf_bin: "",
      order_importance: "",
      no_stock: false,
      has_stock: false,
      markup_operator: "<=",
      markup_value: ""
    });
  };

  const applyMarkupPreset = (value) => {
    const presetValue = String(value);
    setFilterMarkupOperator("<=");
    setFilterMarkupValue(presetValue);
    fetchProducts(searchQuery, 1, false, {
      supplier: filterSupplier,
      category: filterCategory,
      department: filterDepartment,
      shelf_bin: filterShelfBin,
      no_stock: filterNoStock,
      has_stock: filterHasStock,
      markup_operator: "<=",
      markup_value: presetValue
    });
  };

  const handleExportFiltered = async () => {
    try {
      const params = {
        search: searchQuery || undefined,
        supplier: filterSupplier || undefined,
        category: filterCategory || undefined,
        department: filterDepartment || undefined,
        shelf_bin: filterShelfBin || undefined,
        no_stock: filterNoStock || undefined,
        has_stock: filterHasStock || undefined,
        markup_operator: filterMarkupValue !== "" ? filterMarkupOperator : undefined,
        markup_value: filterMarkupValue !== "" && !Number.isNaN(Number(filterMarkupValue))
          ? Number(filterMarkupValue)
          : undefined
      };

      const response = await axios.get(`${API}/products/export`, {
        params,
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `products_filtered_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Filtered product export downloaded");
    } catch (error) {
      console.error("Error exporting filtered products:", error);
      toast.error("Failed to export filtered products");
    }
  };

  const hasActiveFilters = !!(
    filterSupplier ||
    filterCategory ||
    filterDepartment ||
    filterShelfBin ||
    filterImportance ||
    filterNoStock ||
    filterHasStock ||
    filterMarkupValue !== ""
  );

  const activeFilterCount = [
    filterSupplier,
    filterCategory,
    filterDepartment,
    filterShelfBin,
    filterImportance,
    filterNoStock ? "no_stock" : "",
    filterHasStock ? "has_stock" : "",
    filterMarkupValue !== "" ? `markup_${filterMarkupOperator}_${filterMarkupValue}` : ""
  ].filter(Boolean).length;

  const runAutoImportance = async () => {
    if (!window.confirm("Work out order importance from the last 12 months' sales (Rand value)?\n\nTop sellers making up 80% of sales = 1, next 15% = 2, the rest = 3.\nProducts you set by hand are NOT changed.")) return;
    setAutoImportanceBusy(true);
    try {
      const { data } = await axios.post(`${API}/products/order-importance/auto`);
      toast.success(`Importance set: ${data.counts["1"]} × 1 · ${data.counts["2"]} × 2 · ${data.counts["3"]} × 3 (${data.manual_kept} set by hand kept)`);
      fetchProducts(searchQuery, 1, false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to auto-set importance");
    } finally {
      setAutoImportanceBusy(false);
    }
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      fetchProducts(searchQuery, page + 1, true);
    }
  };

  const handleCalculateMissingMarkups = async () => {
    try {
      const response = await axios.post(`${API}/products/calculate-missing-markups`);
      toast.success(`Updated ${response.data.updated_count} products with missing markups`);
      fetchProducts(searchQuery, 1, false);
    } catch (error) {
      toast.error("Failed to calculate markups");
    }
  };

  const handleRecalculateAll = async (checkAll = false) => {
    const filterLabel = filterSupplier || filterCategory || filterDepartment ? " (filtered)" : "";
    const confirmed = window.confirm(
      `Recalculate stock levels for ALL products${filterLabel}?\n\n` +
      "• Uses last 3 months + same 3 months last year of sales\n" +
      "• Applies your Stock Formula settings (Settings → Stock Formula)\n" +
      "• Skips products with no sales history\n" +
      "• Can take 1–3 minutes on a large catalogue\n\n" +
      "Continue?"
    );
    if (!confirmed) return;
    try {
      toast.info(`Bulk recalculation started${filterLabel}… please don't close this tab.`);
      let url = `${API}/products/recalculate-all-auto-levels?check_all=${checkAll}&limit=10000`;
      if (filterSupplier) url += `&supplier=${encodeURIComponent(filterSupplier)}`;
      if (filterCategory) url += `&category=${encodeURIComponent(filterCategory)}`;
      if (filterDepartment) url += `&department=${encodeURIComponent(filterDepartment)}`;

      const response = await saveWithRetry(
        () => axios.post(url, null, { timeout: 240000 }),  // 4 min ceiling
        { retries: 2 }
      );
      const d = response.data || {};
      const processed = d.processed ?? d.total_products_checked ?? 0;
      const updated = d.updated ?? 0;
      const skipped = Math.max(0, processed - updated);
      toast.success(
        `Stock level recalculation complete — ${processed} products checked, ${updated} updated, ${skipped} skipped (no sales history).`,
        { duration: 7000 }
      );
      fetchProducts(searchQuery, 1, false);
    } catch (error) {
      console.error("Recalculate stock levels failed", error);
      toast.error(errorMessage(error, "Failed to recalculate stock levels"));
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsNewProduct(false);
    setDialogOpen(true);
  };

  const handleAddProduct = () => {
    // Create a fresh copy of defaultProduct to ensure form is completely cleared
    const freshProduct = {
      ...defaultProduct,
      barcodes: [], // Ensure a new array reference
      id: null,  // Explicitly set id to null for new products
    };
    setSelectedProduct(freshProduct);
    setIsNewProduct(true);
    setDialogOpen(true);
  };

  const handleSave = async (productData) => {
    try {
      if (isNewProduct) {
        await axios.post(`${API}/products`, productData);
        toast.success("Product created successfully");
      } else {
        await axios.put(`${API}/products/${productData.id}`, productData);
        toast.success("Product updated successfully");
      }
      setDialogOpen(false);
      setIsNewProduct(false);
      fetchProducts(searchQuery, 1, false);
    } catch (error) {
      // Handle Pydantic validation errors which return an array of error objects
      const errorData = error.response?.data;
      let errorMessage = "Failed to save product";
      
      if (errorData) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors return array of {loc, msg, type}
          errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
        } else if (typeof errorData.detail === 'object' && errorData.detail.msg) {
          errorMessage = errorData.detail.msg;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Inline yard-stock toggle from the Stock Master list. Fires a PATCH-style
  // update on the product without opening the full edit modal — persists
  // instantly and refreshes the local list so the amber "YARD" badge sticks.
  const handleToggleYardStock = async (product) => {
    const newValue = !product.is_yard_stock;
    // Optimistic UI: flip the flag locally so the user sees instant feedback.
    setProducts((prev) =>
      prev.map((p) => (p.id === product.id ? { ...p, is_yard_stock: newValue } : p))
    );
    setFilteredProducts((prev) =>
      prev.map((p) => (p.id === product.id ? { ...p, is_yard_stock: newValue } : p))
    );
    try {
      await axios.put(`${API}/products/${product.id}`, { is_yard_stock: newValue });
      toast.success(
        newValue
          ? `${product.name}: marked as yard stock (2 receipt copies at till)`
          : `${product.name}: yard-stock flag removed`
      );
    } catch (error) {
      // Roll back optimistic update on failure
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, is_yard_stock: !newValue } : p))
      );
      setFilteredProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, is_yard_stock: !newValue } : p))
      );
      toast.error(error.response?.data?.detail || "Failed to update yard-stock flag");
    }
  };

  const handleDeleteClick = (e, product) => {
    e.stopPropagation();
    if ((product.current_stock || 0) !== 0) {
      toast.error("Cannot delete product with stock. Stock must be 0.");
      return;
    }
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/products/${productToDelete.id}`);
      toast.success(`Product "${productToDelete.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      fetchProducts(searchQuery, 1, false);
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(error.response?.data?.detail || "Failed to delete product");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveFormulaSettings = async () => {
    try {
      await axios.put(`${API}/settings/stock-formula`, formulaSettings);
      toast.success("Formula settings saved");
      setSettingsDialogOpen(false);
    } catch (error) {
      console.error("Error saving formula settings:", error);
      toast.error("Failed to save settings");
    }
  };

  // Bulk selection handlers
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const getSelectedDeletableCount = () => {
    return filteredProducts.filter(p => 
      selectedProducts.has(p.id) && (p.current_stock || 0) === 0
    ).length;
  };

  const handleBulkDelete = async () => {
    const deletableProducts = filteredProducts.filter(p => 
      selectedProducts.has(p.id) && (p.current_stock || 0) === 0
    );
    
    if (deletableProducts.length === 0) {
      toast.error("No selected products can be deleted (stock must be 0)");
      return;
    }

    setBulkDeleting(true);
    try {
      const productIds = deletableProducts.map(p => p.id);
      const response = await axios.post(`${API}/products/bulk-delete`, productIds);
      
      const { deleted, failed } = response.data;
      
      if (deleted.length > 0) {
        toast.success(`Successfully deleted ${deleted.length} product(s)`);
      }
      if (failed.length > 0) {
        toast.warning(`Failed to delete ${failed.length} product(s)`);
      }
      
      setBulkDeleteDialogOpen(false);
      setSelectedProducts(new Set());
      fetchProducts(searchQuery, 1, false);
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Failed to delete products");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Modals rendering
  const modals = (
    <>
      <ShelfTalkerModal
        open={printStickersOpen}
        onClose={() => setPrintStickersOpen(false)}
        products={selectedForPrint}
      />
      <StockAdjustmentModal
        open={stockAdjustmentOpen}
        onClose={() => {
          setStockAdjustmentOpen(false);
          fetchProducts(searchQuery, 1, false);
        }}
      />
      
      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Multiple Products
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                You are about to delete <strong>{getSelectedDeletableCount()}</strong> product(s) out of <strong>{selectedProducts.size}</strong> selected.
              </p>
              {selectedProducts.size - getSelectedDeletableCount() > 0 && (
                <p className="text-amber-600">
                  Note: {selectedProducts.size - getSelectedDeletableCount()} product(s) cannot be deleted because they have stock on hand.
                </p>
              )}
              <p className="font-semibold">This action cannot be undone!</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting || getSelectedDeletableCount() === 0}
              className="bg-red-600 hover:bg-red-700"
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${getSelectedDeletableCount()} Product(s)`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Sticky Header Row Only - positioned below the navbar (h-12 + h-10 + borders ≈ 90px) */}
      <div className="sticky top-[90px] z-20 bg-white pb-4 -mx-6 px-6 -mt-6 pt-6 border-b shadow-sm">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" />
            Product Master
          </h1>
          <div className="flex flex-wrap gap-2">
            <Link to="/stock-level-editor">
              <Button 
                variant="outline" 
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid="stock-level-editor-btn"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Stock Levels
              </Button>
            </Link>
            <Button
              variant={directOrderMode === "only" ? "default" : "outline"}
              onClick={() => {
                const next = directOrderMode === "only" ? "exclude" : "only";
                setDirectOrderMode(next);
                fetchProducts(searchQuery, 1, false, { direct_order: next });
              }}
              className={directOrderMode === "only"
                ? "bg-purple-600 hover:bg-purple-700 text-white"
                : "text-purple-700 border-purple-300 hover:bg-purple-50"}
              data-testid="direct-items-toggle-btn"
              title={directOrderMode === "only"
                ? "Showing only special-order items — click to return to normal Stock Master"
                : "Show only Special Order / Direct items"}
            >
              <Package className="h-4 w-4 mr-2" />
              {directOrderMode === "only" ? "← Back to Stock" : "Direct Items"}
            </Button>
            <Button
              variant={needsReviewMode ? "default" : "outline"}
              onClick={() => {
                const next = !needsReviewMode;
                setNeedsReviewMode(next);
                fetchProducts(searchQuery, 1, false, { needs_stock_review: next });
              }}
              className={needsReviewMode
                ? "bg-amber-600 hover:bg-amber-700 text-white"
                : "text-amber-700 border-amber-300 hover:bg-amber-50"}
              data-testid="needs-review-toggle-btn"
              title={needsReviewMode
                ? "Showing only products awaiting first-sale stock-level review — click to clear filter"
                : "Show products auto-seeded 1/1/1 on first sale (pending review)"}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              {needsReviewMode ? "← Show All" : "Needs Review"}
            </Button>
            <Button
              variant={showInternalConsumables ? "default" : "outline"}
              onClick={() => {
                const next = !showInternalConsumables;
                setShowInternalConsumables(next);
                fetchProducts(searchQuery, 1, false, { include_internal_consumables: next });
              }}
              className={showInternalConsumables
                ? "bg-slate-700 hover:bg-slate-800 text-white"
                : "text-slate-700 border-slate-300 hover:bg-slate-50"}
              data-testid="show-internal-consumables-toggle"
              title={showInternalConsumables
                ? "Showing internal-use items (till rolls / paper / toner). Click to hide."
                : "Show internal-use items (till rolls, paper, toner) — hidden by default so the Stock Master stays focused on sellable products."}
            >
              Internal Items
            </Button>
            <Button variant="outline" onClick={() => setStockAdjustmentOpen(true)} data-testid="stock-adjustment-btn">
              Stock Adjustment
            </Button>
            <Button
              variant="outline"
              onClick={() => setTopMoversOpen(true)}
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
              data-testid="top-movers-btn"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Top Movers
            </Button>
            <Button
              variant="outline"
              onClick={() => setCleanupOpen(true)}
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="cleanup-unused-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              Cleanup Unused
            </Button>
            <Button
              variant="outline"
              onClick={() => setArchivedOpen(true)}
              className="text-slate-700 border-slate-300 hover:bg-slate-50"
              data-testid="view-archived-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              View Archived
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setQuickCategoryOpen(true)}
              className="text-purple-600 border-purple-300 hover:bg-purple-50"
              data-testid="quick-category-btn"
            >
              <Layers className="h-4 w-4 mr-2" />
              Quick Cat/Dept
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setBulkCategoryOpen(true)}
              className="text-purple-600 border-purple-300 hover:bg-purple-50"
              data-testid="bulk-category-btn"
            >
              <Layers className="h-4 w-4 mr-2" />
              Bulk Cat/Dept
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setDeptFixerOpen(true)}
                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="fix-departments-btn"
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Fix Departments
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setTidyNamesOpen(true)}
                className="text-sky-700 border-sky-300 hover:bg-sky-50"
                data-testid="tidy-dept-names-btn"
              >
                <CaseSensitive className="h-4 w-4 mr-2" />
                Tidy Names
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={runAutoImportance}
                disabled={autoImportanceBusy}
                className="text-red-700 border-red-200 hover:bg-red-50"
                title="Work out order importance from the last 12 months' sales value. Numbers you set by hand are kept."
                data-testid="auto-order-importance-btn"
              >
                {autoImportanceBusy ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Gauge className="h-4 w-4 mr-2" />}
                Auto Importance
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => setQuickLocationOpen(true)}
              className="text-green-600 border-green-300 hover:bg-green-50"
              data-testid="quick-location-btn"
            >
              <Zap className="h-4 w-4 mr-2" />
              Quick Location
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setBulkGroupModalOpen(true)}
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
              data-testid="bulk-groups-btn"
            >
              <Tags className="h-4 w-4 mr-2" />
              Product Groups
            </Button>
            <Button
              variant="outline"
              onClick={() => setBulkSupplierModalOpen(true)}
              className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
              data-testid="bulk-assign-supplier-btn"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Bulk Supplier
            </Button>
            <Button
              variant="outline"
              onClick={() => setBulkStaffDiscountModalOpen(true)}
              className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
              data-testid="bulk-staff-discount-btn"
            >
              <Percent className="h-4 w-4 mr-2" />
              Bulk Staff %
            </Button>
            <Button
              variant="outline"
              onClick={() => setDuplicateCategoriesOpen(true)}
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="duplicate-categories-btn"
            >
              <Tags className="h-4 w-4 mr-2" />
              Clean Up Categories
            </Button>
            <Button
              variant="outline"
              onClick={() => setDuplicateDepartmentsOpen(true)}
              className="text-amber-700 border-amber-300 hover:bg-amber-50"
              data-testid="duplicate-departments-btn"
            >
              <Tags className="h-4 w-4 mr-2" />
              Clean Up Departments
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleRecalculateAll(true)}
              className="text-purple-600"
              data-testid="recalculate-all-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Recalculate All
            </Button>
            <Button 
              variant="outline" 
              onClick={async () => {
                try {
                  let url = `${API}/products/recalculate-markups`;
                  const params = [];
                  if (filterSupplier) params.push(`supplier=${encodeURIComponent(filterSupplier)}`);
                  if (filterCategory) params.push(`category=${encodeURIComponent(filterCategory)}`);
                  if (filterDepartment) params.push(`department=${encodeURIComponent(filterDepartment)}`);
                  if (params.length) url += `?${params.join('&')}`;
                  const res = await axios.post(url);
                  const filterLabel = filterSupplier || filterCategory || filterDepartment ? " (filtered)" : "";
                  toast.success(`Fixed ${res.data.markups_corrected} markups out of ${res.data.total_products} products${filterLabel}`);
                  fetchProducts();
                } catch { toast.error("Failed to recalculate markups"); }
              }}
              className="text-orange-600"
              data-testid="fix-markups-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Fix Markups
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setSettingsDialogOpen(true)}
              data-testid="formula-settings-btn"
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button onClick={handleAddProduct} className="bg-green-600 hover:bg-green-700" data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>
      </div>

      {/* Search - NOT sticky */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, PLU code, or barcode..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? "bg-blue-600 hover:bg-blue-700" : ""}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters {hasActiveFilters && `(${activeFilterCount})`}
          </Button>
        </div>

        {/* Filter Row */}
        {showFilters && (
          <div className="flex flex-wrap gap-3 p-3 bg-gray-50 rounded-lg border" data-testid="filter-row">
            {/* No Stock Filter */}
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border">
              <Checkbox
                id="no-stock-filter"
                checked={filterNoStock}
                onCheckedChange={(checked) => handleFilterChange('no_stock', checked)}
                data-testid="filter-no-stock"
              />
              <label 
                htmlFor="no-stock-filter" 
                className="text-sm font-medium cursor-pointer flex items-center gap-1"
              >
                <PackageX className="h-4 w-4 text-red-500" />
                No Stock on Hand
              </label>
            </div>

            {/* Has Stock Filter — mirror of "No Stock on Hand". Toggling one clears the other. */}
            <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border">
              <Checkbox
                id="has-stock-filter"
                checked={filterHasStock}
                onCheckedChange={(checked) => handleFilterChange('has_stock', checked)}
                data-testid="filter-has-stock"
              />
              <label
                htmlFor="has-stock-filter"
                className="text-sm font-medium cursor-pointer flex items-center gap-1"
              >
                <PackageCheck className="h-4 w-4 text-green-600" />
                Only With Stock
              </label>
            </div>

            {/* Supplier Filter */}
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Supplier</label>
              <Select value={filterSupplier || "all"} onValueChange={(v) => handleFilterChange('supplier', v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-supplier">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {[...suppliers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Category</label>
              <Select value={filterCategory || "all"} onValueChange={(v) => handleFilterChange('category', v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {[...categories].sort((a, b) => ((a.name || a) || '').localeCompare((b.name || b) || '')).map((c) => (
                    <SelectItem key={c.id || c} value={c.name || c}>{c.name || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>


            {/* Markup Filter (Custom) */}
            <div className="min-w-[320px]" data-testid="markup-filter-container">
              <label className="text-xs text-gray-500 mb-1 block">Markup Filter</label>
              <div className="flex gap-2 items-center">
                <Select
                  value={filterMarkupOperator}
                  onValueChange={(v) => handleFilterChange('markup_operator', v)}
                >
                  <SelectTrigger className="w-20" data-testid="filter-markup-operator">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="<">&lt;</SelectItem>
                    <SelectItem value="<=">&lt;=</SelectItem>
                    <SelectItem value="=">=</SelectItem>
                    <SelectItem value=">=">&gt;=</SelectItem>
                    <SelectItem value=">">&gt;</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Enter %"
                  value={filterMarkupValue}
                  onChange={(e) => handleFilterChange('markup_value', e.target.value)}
                  className="w-28"
                  data-testid="filter-markup-value"
                />
                <span className="text-sm text-gray-500">%</span>
                <div className="flex gap-1 ml-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => applyMarkupPreset(5)}
                    data-testid="markup-preset-5"
                  >
                    ≤5%
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => applyMarkupPreset(10)}
                    data-testid="markup-preset-10"
                  >
                    ≤10%
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => applyMarkupPreset(20)}
                    data-testid="markup-preset-20"
                  >
                    ≤20%
                  </Button>
                </div>
              </div>
            </div>

            {/* Export Filtered */}
            <div className="flex items-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportFiltered}
                className="text-blue-700 border-blue-300 hover:bg-blue-50"
                data-testid="export-filtered-products-btn"
              >
                Export Filtered CSV
              </Button>
            </div>

            {/* Department Filter */}
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Department</label>
              <Select value={filterDepartment || "all"} onValueChange={(v) => handleFilterChange('department', v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-department">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {[...departments].sort((a, b) => ((a.name || a) || '').localeCompare((b.name || b) || '')).map((d) => (
                    <SelectItem key={d.id || d} value={d.name || d}>{d.name || d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Shelf/Bin Filter */}
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Shelf / Bin</label>
              <Select value={filterShelfBin || "all"} onValueChange={(v) => handleFilterChange('shelf_bin', v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-shelf-bin">
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {[...shelfBins].sort((a, b) => a.localeCompare(b)).map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Order Importance Filter */}
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 mb-1 block">Order Importance</label>
              <Select value={filterImportance || "all"} onValueChange={(v) => handleFilterChange('order_importance', v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-order-importance">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1">1 — Very important</SelectItem>
                  <SelectItem value="2">2 — Important</SelectItem>
                  <SelectItem value="3">3 — Not so important</SelectItem>
                  <SelectItem value="none">Not set</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-4">
            {/* Select All Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all"
                checked={filteredProducts.length > 0 && selectedProducts.size === filteredProducts.length}
                onCheckedChange={toggleSelectAll}
                data-testid="select-all-checkbox"
              />
              <label htmlFor="select-all" className="text-sm text-gray-600 cursor-pointer">
                Select All
              </label>
            </div>
            
            <p className="text-sm text-gray-600">
              Showing {filteredProducts.length} of {totalProducts} products
              {searchQuery && ` matching "${searchQuery}"`}
              {hasActiveFilters && " (filtered)"}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Bulk Delete Button - only show when items selected */}
            {selectedProducts.size > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkVatOpen(true)}
                  className="text-amber-700 border-amber-400 hover:bg-amber-50"
                  data-testid="bulk-vat-btn"
                >
                  <Calculator className="h-4 w-4 mr-1" />
                  Bulk VAT ({selectedProducts.size})
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Selected ({getSelectedDeletableCount()}/{selectedProducts.size})
                </Button>
              </>
            )}
            {loading && <span className="text-sm text-blue-600 animate-pulse">Loading...</span>}
          </div>
        </div>
      </div>

      {/* Products List */}
      <ProductList
        products={filteredProducts}
        loading={loading}
        hasMore={hasMore}
        totalProducts={totalProducts}
        searchQuery={searchQuery}
        onProductClick={handleProductClick}
        onDeleteClick={handleDeleteClick}
        onLoadMore={loadMore}
        onAddProduct={handleAddProduct}
        selectedProducts={selectedProducts}
        onToggleSelection={toggleProductSelection}
        onToggleYardStock={handleToggleYardStock}
      />

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setIsNewProduct(false); }}
          product={selectedProduct}
          onSave={handleSave}
          categories={categories}
          departments={departments}
          onRefreshLists={fetchCategoriesDepartments}
          isNew={isNewProduct}
          onRefreshProducts={() => fetchProducts(searchQuery, 1, false)}
        />
      )}

      {/* Formula Settings Dialog */}
      <FormulaSettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
        formulaSettings={formulaSettings}
        setFormulaSettings={setFormulaSettings}
        onSave={handleSaveFormulaSettings}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteProductDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        productToDelete={productToDelete}
        deleting={deleting}
        onConfirmDelete={handleConfirmDelete}
      />

      {/* Quick Location Editor */}
      <QuickLocationEditor
        open={quickLocationOpen}
        onClose={() => setQuickLocationOpen(false)}
        products={products}
        onProductUpdated={() => fetchProducts(searchQuery, 1, false)}
      />

      {/* Quick Category Editor */}
      <QuickCategoryEditor
        open={quickCategoryOpen}
        onClose={() => setQuickCategoryOpen(false)}
        categories={categories}
        departments={departments}
        onProductUpdated={() => fetchProducts(searchQuery, 1, false)}
        keepLocked={keepCategoryLocked}
        setKeepLocked={setKeepCategoryLocked}
        lockedCategory={lockedCategoryValue}
        setLockedCategory={setLockedCategoryValue}
        lockedDepartment={lockedDepartmentValue}
        setLockedDepartment={setLockedDepartmentValue}
      />

      {/* Bulk Category/Department Assignment Modal */}
      <BulkCategoryDepartmentModal
        open={bulkCategoryOpen}
        onClose={() => setBulkCategoryOpen(false)}
        categories={categories}
        departments={departments}
        onComplete={() => fetchProducts(searchQuery, 1, false)}
      />

      <DepartmentFixerModal
        open={deptFixerOpen}
        onClose={() => setDeptFixerOpen(false)}
        onComplete={() => fetchProducts(searchQuery, 1, false)}
      />
      <TidyDepartmentNamesModal
        open={tidyNamesOpen}
        onClose={() => setTidyNamesOpen(false)}
        onDone={() => fetchProducts(searchQuery, 1, false)}
      />

      {/* Bulk VAT Category Editor — the one-off cleanup tool the shop owner uses
          to flip zero-rated basket / snuff / etc. to the correct VAT treatment
          across many products at once. Uses the same select-in-list flow as
          Bulk Delete. */}
      <Dialog open={bulkVatOpen} onOpenChange={setBulkVatOpen}>
        <DialogContent data-testid="bulk-vat-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-amber-600" />
              Bulk Set VAT Category
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              You have selected <strong>{selectedProducts.size}</strong> product{selectedProducts.size === 1 ? "" : "s"}.
              Pick the VAT category to apply to every one of them. The change
              takes effect on future GRVs and till slips immediately.
            </p>
            <div>
              <Label className="text-xs uppercase text-gray-600">Target VAT Category</Label>
              <Select value={bulkVatTarget} onValueChange={setBulkVatTarget}>
                <SelectTrigger data-testid="bulk-vat-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard 15%">Standard 15%</SelectItem>
                  <SelectItem value="Zero-rated 0%">Zero-rated 0%</SelectItem>
                  <SelectItem value="Exempt">Exempt</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              <strong>Reminder:</strong> the SA zero-rated basic-food basket
              includes brown bread, mielies / maize meal, samp, rice, dried
              beans, lentils, fresh fruit &amp; vegetables, fresh milk, eggs,
              cooking oil, brown wheaten meal, tinned pilchards and edible legumes.
              Everything else is normally Standard&nbsp;15%.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkVatOpen(false)} disabled={bulkVatBusy}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              disabled={bulkVatBusy}
              data-testid="bulk-vat-apply"
              onClick={async () => {
                setBulkVatBusy(true);
                try {
                  const res = await axios.post(`${API}/products/bulk-set-vat-category`, {
                    product_ids: Array.from(selectedProducts),
                    vat_category: bulkVatTarget,
                  });
                  toast.success(res.data?.message || "VAT category updated.");
                  setBulkVatOpen(false);
                  setSelectedProducts(new Set());
                  fetchProducts(searchQuery, 1, false);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to update VAT category.");
                } finally {
                  setBulkVatBusy(false);
                }
              }}
            >
              {bulkVatBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Calculator className="h-4 w-4 mr-1" />}
              Apply to {selectedProducts.size} product{selectedProducts.size === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Product Group Assignment Modal */}
      <BulkProductGroupModal
        open={bulkGroupModalOpen}
        onClose={() => setBulkGroupModalOpen(false)}
        categories={categories}
        onComplete={() => fetchProducts(searchQuery, 1, false)}
      />

      {/* Cleanup Unused Products Modal */}
      <UnusedProductsCleanup
        open={cleanupOpen}
        onClose={() => setCleanupOpen(false)}
        onProductsChanged={() => fetchProducts(searchQuery, 1, false)}
      />

      {/* Archived Products Restore Modal */}
      <ArchivedProductsModal
        open={archivedOpen}
        onOpenChange={setArchivedOpen}
        onRestored={() => fetchProducts(searchQuery, 1, false)}
      />

      <TopMoversModal
        open={topMoversOpen}
        onOpenChange={setTopMoversOpen}
        suppliers={suppliers}
      />

      <BulkAssignSupplierModal
        open={bulkSupplierModalOpen}
        onClose={() => setBulkSupplierModalOpen(false)}
        suppliers={suppliers}
        onComplete={() => fetchProducts(searchQuery, 1, false)}
      />

      <BulkStaffDiscountModal
        open={bulkStaffDiscountModalOpen}
        onClose={() => setBulkStaffDiscountModalOpen(false)}
        suppliers={suppliers}
        categories={categories}
        departments={departments}
        onComplete={() => fetchProducts(searchQuery, 1, false)}
      />

      <DuplicateCategoriesDialog
        open={duplicateCategoriesOpen}
        onClose={() => setDuplicateCategoriesOpen(false)}
        onComplete={() => fetchProducts(searchQuery, 1, false)}
      />

      <DuplicateDepartmentsDialog
        open={duplicateDepartmentsOpen}
        onClose={() => setDuplicateDepartmentsOpen(false)}
        onComplete={() => fetchProducts(searchQuery, 1, false)}
      />

      {modals}
    </div>
  );
};

export default ProductMaster;
